import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { brandApi } from '@/api/account-management/brandApi';
import { type AppType } from '@/models/enums/AppType';
import { appTypeLabel } from '@/models/media-network/appLabels';
import { contentStringApi, contentStringKeys } from '@/api/skin/contentStringApi';
import { type Brand } from '@/models/account-management/Brand';
import {
    type BrandContentString, type ContentStringCategory, type ContentStringKey,
    displayValueFor, FALLBACK_LOCALE, originFor, parseLocaleValues,
} from '@/models/skin/ContentString';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';
import { fillHeight } from '../../theme';
import ContentStringDrawer from './dialogs/ContentStringDrawer';

interface Row {
    id: string;
    key: string;
    categoryName: string;
    appTypes: number[];
    placeholders: string[];
    brandValues: Record<string, string>;
    defaultValues: Record<string, string>;
    /** True when any shown language lacks its own text - either falling back to en-US, or nothing at all. */
    hasGap: boolean;
}

const ContentStrings = () => {
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();
    const canUpdate = perms.canUpdate(Perm.configurationContentStrings);

    const brandId = app.brandId ?? undefined;

    const [categoryId, setCategoryId] = useState<string>('');
    const [search, setSearch] = useState('');
    const [onlyMissing, setOnlyMissing] = useState(false);
    const [editing, setEditing] = useState<Row | null>(null);

    const { data: categories } = useQuery<ContentStringCategory[]>({
        queryKey: contentStringKeys.categories(),
        queryFn: () => contentStringApi.listCategories(),
    });

    const { data: keys, isPending: keysPending } = useQuery<ContentStringKey[]>({
        queryKey: contentStringKeys.keys(),
        queryFn: () => contentStringApi.listKeys(),
    });

    const { data: overrides, isPending: overridesPending } = useQuery<BrandContentString[]>({
        queryKey: contentStringKeys.brandOverrides(brandId ?? ''),
        enabled: !!brandId,
        queryFn: () => contentStringApi.listBrandOverrides(brandId!),
        staleTime: 0,
    });

    // The language columns are the brand's DECLARED supported locales, set on Brand Details.
    //
    // They used to be derived from the brand's existing locations, which had a hole: you could not translate
    // for a language until you had already opened a location there, so a brand preparing to launch in Quebec
    // had no way to write French copy in advance.
    const { data: brand } = useQuery<Brand>({
        queryKey: ['brand-for-locales', brandId],
        enabled: !!brandId,
        queryFn: () => brandApi.get(brandId!),
    });

    const locales = useMemo(() => {
        const declared = brand?.supportedLocales ?? [];

        // en-US first and always present, even when the brand has not declared it. It is the last step of the
        // resolution chain, so hiding it would hide the text a French location actually gets when its own
        // translation is missing - the "falls back to en-US" state would have nothing to point at.
        return [FALLBACK_LOCALE, ...declared].reduce<string[]>((acc, locale) => {
            if (!acc.some(x => x.toLowerCase() === locale.toLowerCase())) acc.push(locale);
            return acc;
        }, []);
    }, [brand]);

    const rows: Row[] = useMemo(() => {
        if (!keys) return [];

        const overrideByKeyId = new Map((overrides ?? []).map(o => [o.contentStringKeyId, o.stringJson]));

        return keys.map(k => {
            const brandValues = parseLocaleValues(overrideByKeyId.get(k.id));
            const defaultValues = parseLocaleValues(k.defaultStringJson);
            // Both gap states count: 'fallback' is the one a translator is actually hunting for - the app is
            // rendering English on a French screen - and 'missing' is worse still. Filtering on 'missing' alone
            // would hide every untranslated string that happens to have an English default, which is all of them.
            const hasGap = locales.some(l => {
                const origin = originFor(brandValues, defaultValues, l);
                return origin === 'fallback' || origin === 'missing';
            });

            return {
                id: k.id,
                key: k.key,
                categoryName: k.categoryName,
                appTypes: k.appTypes,
                // ?? [] because the portal and PortalAPI deploy independently: until the API that
                // returns this ships, the field is simply absent, and a bare .map on it would take out
                // the whole page rather than degrade to "no placeholders".
                placeholders: k.placeholders ?? [],
                brandValues,
                defaultValues,
                hasGap,
            };
        });
    }, [keys, overrides, locales]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return rows.filter(r => {
            if (categoryId && keys?.find(k => k.id === r.id)?.categoryId !== categoryId) return false;
            if (onlyMissing && !r.hasGap) return false;
            if (term && !r.key.toLowerCase().includes(term)) return false;
            return true;
        });
    }, [rows, keys, categoryId, onlyMissing, search]);

    const columns: GridColDef<Row>[] = useMemo(() => {
        const localeColumns: GridColDef<Row>[] = locales.map(locale => ({
            field: `locale-${locale}`,
            headerName: locale,
            flex: 2,
            sortable: false,
            renderCell: params => {
                const origin = originFor(params.row.brandValues, params.row.defaultValues, locale);
                const value = displayValueFor(params.row.brandValues, params.row.defaultValues, locale);

                // Nothing anywhere - not even an en-US default. This is the only case where the app really
                // does render the key name, so it is the only one shown as an error.
                if (origin === 'missing') {
                    return (
                        <Tooltip title={strings.contentStringMissingHelp}>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
                                <WarningAmberIcon color="error" fontSize="small" />
                                <Typography variant="body2" color="error.main">
                                    {strings.contentStringMissing}
                                </Typography>
                            </Stack>
                        </Tooltip>
                    );
                }

                // Inherited text is shown, not hidden: an empty cell would read as broken, when in fact the
                // app renders something perfectly well. Muted and italic says "this is not yours".
                //
                // 'fallback' gets its own marker because it is the actionable one: the app is showing ENGLISH
                // on a screen in another language. Calling that "missing" would be alarming and wrong;
                // showing it as ordinary inherited text would hide the one thing a translator is looking for.
                const isFallback = origin === 'fallback';

                return (
                    <Tooltip title={isFallback ? strings.contentStringFallbackHelp : ''}>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
                            {isFallback && (
                                <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label={FALLBACK_LOCALE}
                                    sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                                />
                            )}
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                    color: origin === 'brand' ? 'text.primary' : 'text.disabled',
                                    fontStyle: origin === 'brand' ? 'normal' : 'italic',
                                }}
                            >
                                {value}
                            </Typography>
                        </Stack>
                    </Tooltip>
                );
            },
        }));

        return [
            { field: 'key', headerName: strings.contentStringKey, flex: 3 },
            {
                field: 'categoryName',
                headerName: strings.category,
                flex: 1,
                renderCell: params => <Chip size="small" label={params.row.categoryName} />,
            },
            {
                // Which apps request this string. Without it a customer cannot tell whether the text they are
                // editing appears on the tablet, the Dual Display, or nowhere they will ever look.
                field: 'appTypes',
                headerName: strings.contentStringApps,
                flex: 2,
                sortable: false,
                renderCell: params => (
                    <Stack direction="row" spacing={0.5} sx={{ height: '100%', alignItems: 'center', flexWrap: 'nowrap', overflow: 'hidden' }}>
                        {params.row.appTypes.map(a => (
                            <Chip key={a} size="small" variant="outlined" label={appTypeLabel(a as AppType)} />
                        ))}
                    </Stack>
                ),
            },
            ...localeColumns,
        ];
    }, [locales, strings]);

    if (!brandId) {
        return (
            <Box sx={{ ...fillHeight, p: 2 }}>
                <Alert severity="info">{strings.contentStringSelectBrand}</Alert>
            </Box>
        );
    }

    // No page heading: the title bar breadcrumb already names this screen, and repeating it just costs a row
    // of vertical space the grid could use.
    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    select
                    size="small"
                    label={strings.category}
                    value={categoryId}
                    onChange={e => setCategoryId(e.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="">{strings.all}</MenuItem>
                    {(categories ?? []).map(c => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    size="small"
                    label={strings.search}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{ minWidth: 240 }}
                />

                {/* The translation workflow in one checkbox: someone adding a language wants the handful of
                    keys that lack it, not a browse through every key that already reads fine. */}
                <FormControlLabel
                    control={<Checkbox checked={onlyMissing} onChange={e => setOnlyMissing(e.target.checked)} />}
                    label={strings.contentStringOnlyMissing}
                />
            </Stack>

            <DataGrid
                rows={filtered}
                columns={columns}
                loading={keysPending || overridesPending}
                disableRowSelectionOnClick
                onRowClick={(params: GridRowParams<Row>) => setEditing(params.row)}
                sx={{ cursor: 'pointer' }}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                pageSizeOptions={[25, 50, 100]}
            />

            {editing && (
                <ContentStringDrawer
                    open
                    brandId={brandId}
                    keyId={editing.id}
                    contentKey={editing.key}
                    locales={locales}
                    brandValues={editing.brandValues}
                    defaultValues={editing.defaultValues}
                    placeholders={editing.placeholders}
                    canUpdate={canUpdate}
                    onClose={() => setEditing(null)}
                />
            )}
        </Box>
    );
};

export default ContentStrings;
